import { Config } from 'config';

import { forEach, forOwn, filter, find, assign,
         every, some, now, uniqueId } from 'lodash';

import { OwnedObject, resolve, findByType, invokeMap$ } from 'gear';

import { Xml, Metadata } from '../util';
import { Matrix, Point, Bounds, Frame } from '../geometry';
import { Pen, Brush, Color } from '../style';

import { Processor, OutputProcessor, updateMethods } from './detail/processor';
import { FigureCache } from './detail/cache';

import { Process } from '../process/process';
import { Processes } from '../process/processes';
import { AnchorGenerator, Pin, FigureTransform } from './anchor';

import { Figures } from './figures';

// > 5

const _lock           = function() { this.lock(); },
      _invalidate     = function() { this.invalidate(); },
      _invalidateView = function() { this.invalidateView(); };

export const Figure = OwnedObject.extend( updateMethods(), { typeName: 'Figure', _localId : 0,

  __debugProps: true,

  Properties: {
    pen          : { type: 'Pen'    , def: Pen.empty  , onChange: _invalidate },
    brush        : { type: 'Brush'  , def: Brush.empty, onChange: _invalidate },
    alpha        : { type: 'number' , def: 1.0, dropDef:true, onChange: _invalidateView },
    id           : { type: 'string' , def: null, dropDef:true },
    name         : { type: 'string' , def: null, dropDef:true },
    xType        : { type: 'string' , def: 'CadX.Figure', dropDef:true, onChange: _lock  },
    locked       : { type: 'boolean', def: false, dropDef:true, onChange: _lock  },
    hidden       : { type: 'boolean', def: false, dropDef:true, onChange: _invalidateView },
    clipping     : { type: 'boolean', def: false, dropDef:true, onChange: _invalidate },
    clipAbsolute : { type: 'boolean', def: false, dropDef:true, onChange: _invalidate },
    clippedBy    : { type: 'string' , def: null,  dropdef:true, onChange: _invalidate },
    processes    : { type: 'Process[]', def: [], onChange: _invalidate, array: Processes },

    powerClipContents : { type: 'PowerClipContent[]', def: [], onChange: _invalidate },

    anchor      : { type: 'Anchor', def: new AnchorGenerator(), onChange: _invalidate } ,
    preMatrix22 : { type: 'Matrix22', def: new Matrix(), manualParsing: true, onChange: _invalidate },
    matrix22    : { type: 'Matrix22', def: new Matrix(), manualParsing: true, onChange() { this.invalidateTransform(); } },
    metadata    : { type: 'Metadata', def: new Metadata({}), manualParsing: true },

    // Not part of CDL, Grafika specific properties
    cacheId      : { type: 'string', def: null, manualParsing: true, computed: true },
    outputFigure : { type: 'Figure', def: null, owned: false, manualCloning: true, computed: true, manualParsing: true, onChange() { this._onOutputFigureChange(); } },
    highlight    : { type: 'boolean', def: false, manualParsing: true, computed: true, onChange: _invalidateView },
    editingClip  : { type: 'boolean', def: false, manualParsing: true, computed: true, onChange: _invalidateView }
  },

  get isGrouped() {
    const owner = this._owner;
    return owner && owner.isGroup;
  },
  get isEmpty() {
    return false;
  },
  get isFigure() {
    return true;
  },

// [TODO] pin : we are not making it a real property until the time when bounds based pinning is removed
  get pin() {
    return this._pin.getPos();
  },
  set pin( pin_ ) {
    this.translate( pin_.minus(this.pin) );
  },

  lock( originator) {
    if( this._owner ) {

      this._owner.lock(originator||this);
    }
  },

  update$( type ) {
    if (type === "commit")
    {
      if (this._invalidateOnCommit) {
        this._processor.invalidate();
        this._invalidateOnCommit = false;
      }
    }
    return this._processor.update$(type);
  },

  get visible() {
    return !this._hidden;
  },
  set visible( visible_ ) {
    this._hidden = !visible_;
  },

  containsLinkedResources() {
    return !! find( this.getBrushes(), brush => brush.containsLinkedResources() );
  },

  rotate$( angle ) {
    return this.center$() .then( center => {
    return this.rotateAround(center,angle);
    });
  },

  scale$( sx, sy ) {
    return this.center$() .then( center => {
    this.scaleFrom(center,sx,sy);
    });
  },

  mirrorX$() {
    return this.scale$(-1,1);
  },
  mirrorY$() {
    return this.scale$(1,-1);
  },

  normalizeAngle( angle ) {
    const m = this._matrix22;
    const a = angle, isMirror = m.determinant() < 0;
    const ra = isMirror ? a-180 : a;
    return ra > 180 ? ra-360 : ra < -180 ? ra+360 : ra;
  },

  angle( v, pivot ) {
    const m = this._matrix22;
    const a = m.angle();
    if(v===undefined) {
      return this.normalizeAngle(a);
    }
    else{
      const isMirror = m.determinant() < 0;
      const na = isMirror ? v-180 : v;
      return this.rotateAround(pivot||this.pin,na-a);
    }
  },

  canBeSelected() {
    return ! this._locked && ! this._hidden && ! this.isLockedTemporally() && ! this.clippingHidden();
  },

  // Used by the UI, returns false in case the figure is not processed
  containsPoint( point ) {
    return ( this._outputFigure || this._cacheId ) &&
           this.globalFrame_().contains(point) &&
           this._processor.fineHitTest(point,this.globalMatrix_());
  },

  hitTest( point, options = {} ) {
    if( this.canBeSelected() && this.globalFrame_().contains(point) ) { // [TODO] Use 'selectable'?
      if( options.frameBased || ! Config.useCurvesBasedSelection ) {
        return true;
      }
      return this._processor.fineHitTest(point,this.globalMatrix_(),options);
    }
    return false;
  },

  cloneSwap( figure){
    var c = figure.clone();
    c.pin = this.pin.clone();
    c.anchor = this.anchor.clone();
    c.matrix22 = this.matrix22.clone();
    return c;
  },

  pushLockedTemporally() {
    ++this._lockedTemporally;
  },
  popLockedTemporally() {
    if( this._lockedTemporally > 0 ) {
      --this._lockedTemporally;
    }
  },
  isLockedTemporally() {
    return this._lockedTemporally > 0;
  },

  // [TODO] We need to replace this with forEachColor abstraction
  getColors( colors = [] ) {
    this.topColors(colors);
    this.otherColors(colors) ; // Override in derived classes
    if (this._processes.length > 0 && this.outputFigure && this.outputFigure !== this) {
      this.outputFigure.getColors(colors);
    }
    if (this.isRaster && this.isMonochrome) {
      this.colorMap.getColors(colors);
    }
    this._processes.getColors(colors) ;
    forEach( this.powerClipContents, pcc => {
      pcc.getColors(colors);
    });
    return colors;
  },

  setTaggedColors( tag, color ) {
    // This function goes through the processor to optimize the invalidation
    this._processor.setTaggedColors(tag,color);
    return this;
  },

  _setTaggedColors( tag, color ) {
    const keepTags   = true;
    const keepLabels = true;
    const colors = this.getColors();

    let changed = false;

    //_.forEach(colors,function(c) {
    for(let k=0,kEnd=colors.length;k<kEnd;++k) {const c =colors[k];
      if(c._tag === tag) {
        c._replace(color,keepTags,keepLabels); // avoid figure invalidation
        changed = true;
      }
    }//);
    return changed;
  },

  setLabeledColors( label, color ) {
    // This function goes through the processor to optimize the invalidation
    this._processor.setLabeledColors(label,color);
    return this;
  },

  _setLabeledColors( label, color ) {
    const keepTags   = true;
    const keepLabels = true;
    const colors = this.getColors();

    let changed = false;

    //_.forEach(colors,function(c) {
    for(let k=0,kEnd=colors.length;k<kEnd;++k) {const c =colors[k];
      if(c._label === label) {
        c._replace(color,keepTags,keepLabels); // avoid figure invalidation
        changed = true;
      }
    }//);
    return changed;
  },

  replaceColor(  oldColor, newColor, options ) {
    // [TODO] Simplify? Maybe we do not need this optimization any more
    //
    // This function goes through the processor to optimize the invalidation
    this._processor.replaceColor( oldColor, newColor, options );
    return this;
  },
  _replaceColor: Color._this_fastReplaceColor,

  // Old name, alias to keep bc
  uniqueColorZones: Color._this_zones,

  // Use this one from now on
  colorZones: Color._this_zones,

  changeColorZone( zone, value ) {
    // [TODO] We may not need all this anymore
    //        simplify, keep this only about colors
    //        just use => Color._this_changeZone,
    if( zone._unique ) {
      if( zone.value instanceof Color ) {
        if( value instanceof Color ) {
          this.replaceColor(zone.value,value,{tagsCollapsed:zone.tagsCollapsed,keepTags:true});
        }
        else if( value instanceof Brush ) {
          this.replaceColor(zone.value,value.color,{tagsCollapsed:zone.tagsCollapsed,keepTags:true});
        }
        else{
          this.replaceBrush(new Brush(zone.value.clone()),value);
        }
      }
      else {
        const newBrush = value instanceof Color ? new Brush(value) : value ;
        this.replaceBrush(zone.value,newBrush);
      }
    }/*
    else {
      // To be used by structured color zones
    }*/
  },

  replaceBrush( oldBrush, newBrush ) {
    // This function goes through the processor to optimize the invalidation
    this._processor.replaceBrush( oldBrush, newBrush );
    return this;
  },
  _replaceBrush( oldBrush, newBrush ) {
    const oldBrushString = oldBrush.textualize();
    const brushes = this.getBrushes();

    let changed = false;

    //_.forEach(brushes,function(c) {
    for(let k=0,kEnd=brushes.length;k<kEnd;++k) {const b =brushes[k];
      const bString = b.textualize();
      if( bString === oldBrushString ) {
        // Performance hack
        const owner = b._owner;

        const { tag, label } = b;

        const brush = owner._brush = newBrush.clone();
        brush._owner = owner;

        // Preserve tags and labels, used to represent clipart structure
        // Avoid collapsing brushes into a single color, that later can not
        // be subdivided.
        brush._tag   = tag;
        brush._label = label;

        changed = true;
      }
    }//);
    return changed;
  },

  invalidate() {
    this._processor.invalidate();
    this.callParent();
    return this;
  },
  invalidateGrouping() {
    this._processor.invalidateGrouping();
    return this;
  },
  invalidateTransform(){ 
    this._processor.invalidateTransform();     
    return this; 
  },
  invalidateView( force ) {
    this._processor.invalidateView(force);
    return this;
  },
  invalidateViewTransform() {
    this._processor.invalidateViewTransform();
    return this;
  },

  invalidated() {
    return this._processor.invalidated();
  },
  processed() {
    return this._processor.processed();
  },
  currentlyProcessing() {
    return this._processor.currentlyProcessing();
  },

  _clone( /*partOfGroup, ungrouping*/ ) {
    const p = {};
    const properties = this.Properties;
    //_.forEach(properties,function(prop,name ) { [TODO]
    forOwn(properties, ( prop, name ) => {
      if( prop.manualCloning!==true ) {
        const v = this[prop.member];
        p[name] = v!==null ? v.cloneDeep ? v.cloneDeep() : v.clone ? v.clone() : v : null;
      }
    });
    p.pin = this.pin.clone(); // still outside of properties
    return p;
  },
  clone( includingProcessor, partOfGroup, ungrouping ) {
    if( includingProcessor === undefined ) { includingProcessor = true; }
    if( partOfGroup === undefined ) { partOfGroup = this.isGroup; }
    if( ungrouping === undefined ) { ungrouping = (this._owner instanceof Figure.Group); }

    const clonedFigure = new this.constructor( this._clone(partOfGroup,ungrouping) );
    if( includingProcessor ) {
      clonedFigure._processor = this._processor.clone(clonedFigure,partOfGroup,ungrouping);
    }

    clonedFigure._computedPreScale = this._computedPreScale.clone();

    if( ! partOfGroup ) {
      // Clean up the PowerClipContent cloneLink used in PowerClipLink cloning
      forEach( this.deepPowerClipContents(), pcContent => {
        pcContent._cloneLink = null;
      });
    }
    return clonedFigure;
  },

  toXml( serverCache = true ) {
    const bypassServerCacheAttr = (serverCache?'':( ' _canvasBypassServerCache="'+now()+'"' ));
    const xml = Xml.fromString('<Cdl'+bypassServerCacheAttr+'></Cdl>');
    this.buildXml(xml.documentElement);
    return xml;
  },

  toXmlString( serverCache ) {
    return Xml.toString( this.toXml(serverCache) );
  },

// , buildXml: ... // overriden by derived figures

  setupXml( node ) {
    const me = this;

    Xml.setupProperties(node,this);

    this._metadata.setupXml(node);

    let preMatrix22 = this._preMatrix22;
    if( ! preMatrix22.isIdentity() ) {
      const preTransformNode = Xml.appendElement(node,'PreTransform');
      preMatrix22.setMatrix22Attr(preTransformNode,'Matrix22');
    }
    FigureTransform.buildXml(me._matrix22,node);
    me._pin.buildXml(node) ;
  },

  bakeTransform() {
    this._preMatrix22 = Matrix.m( this._matrix22, this._preMatrix22 );
    this._matrix22 = new Matrix();
    this.invalidate();
  },

  // Options are 'left', 'center', 'right' + 'bottom', 'middle', 'top'.
  align$( alignment, to, margin ) {
    const toBounds = to.bounds || ( to.lx !== undefined ? to : null );
    if( toBounds ) {
      return this._align$(alignment,toBounds,margin);
    }
    else {
      return Figures.bounds$(to).then( toBounds => {
      return this._align$(alignment,toBounds,margin);
      });
    }
  },

  fitFrame$( toFrame, options = {} ) {
    const me = this;
    if( ! options.ignoreWidth  ) { options.width  = toFrame.width ; }
    if( ! options.ignoreHeight ) { options.height = toFrame.height; }
    return me.frame$() .then( frame => {
      const s = Figures._computeSizingScale(frame.width,frame.height,options);
      return me.transform( Matrix.m(
        Matrix.scaleFrom( frame.center, s.sX, s.sY, frame.angle() ),
        Matrix.translate( toFrame.center.minus(frame.center) )
      ) );
    });
  },

  modifyKeeping$( what, func ) {
    return this.update$() .then( () => {

      switch( what ) {
        case 'nothing': {
          return resolve( func(this) ) .then( () => this );
        }
        case 'bounds': {
          const oldBounds = this.bounds_();
          return resolve( func(this) ) .then( () => {
          return this.fit$(oldBounds);
          });
        }
        case 'center': {
          const oldCenter = this.center_();
          return resolve( func(this) ).then( () => {
          return this.center$(oldCenter);
          });
        }
        case 'width': {
          const oldBounds = this.bounds_();
          return resolve( func(this) ).then( () => {
          return this.fit$(oldBounds,{ ignoreHeight: true });
          });
        }
        case 'frame': {
          const oldFrame = this.frame_();
          return resolve( func(this) ).then( () => {
          return this.fitFrame$(oldFrame);
          });
        }
        case 'frameCenter': {
          const oldFrame = this.frame_();
          return resolve( func(this) ).then( () => {
          return this.frame$() .then( frame => {
          return this.translate( oldFrame.center .minus( frame.center ) );
          }); });
        }
        case 'frameWidth': {
          const oldFrame = this.frame_();
          return resolve( func(this) ).then( () => {
          return this.fitFrame$(oldFrame,{ ignoreHeight: true });
          });
        }
        default:
          // error('Figure.modifyKeeping, unsupported option');
      }
    });
  },

  center_() {
    return this.bounds_().center;
  },

  center$( where, y ) {
    return Figures.center$([this],where,y);
  },

// Reference point abstraction, this will be eliminated when we are going to be able to eliminate the bounds based pinning
  preReferencePoint_() {
    return this._cache.get('preReferencePoint' );
  },
  referencePoint_() {
    return this.preReferencePoint_().transformed( this.matrix_() );
  },
  globalPreReferencePoint_() {
    return this.preReferencePoint_().transformed( this.globalPreMatrix_() );
  },
  globalReferencePoint_() {
    return this.preReferencePoint_().transformed( this.globalMatrix_() );
  },
  preReferencePoint$() {
    return this.update$().then( this.preReferencePoint_.bind(this) );
  },
  referencePoint$() {
    return this.update$().then( this.referencePoint_.bind(this) );
  },
  globalPreReferencePoint$() {
    return this.update$().then( this.globalPreReferencePoint_.bind(this) );
  },
  globalReferencePoint$() {
    return this.update$().then( this.globalReferencePoint_.bind(this) );
  },
  _computePreReferencePoint() {
    return this._processor.computePreReferencePoint();
  },
// ----------

  vectorFrame$() {
    return this.update$().then( this.vectorFrame_.bind(this) );
  },
  frame$() {
    return this.update$().then( this.frame_.bind(this) );
  },
  globalVectorFrame$() {
    return this.update$().then( this.globalVectorFrame_.bind(this) );
  },
  globalFrame$() {
    return this.update$().then( this.globalFrame_.bind(this) );
  },

  vectorFrame_() {
    return Frame.fromBounds(this.preVectorBounds_()).transformed( this.matrix_() );
  },
  frame_() {
    const matrix = this.matrix_();
    
    // if (this.getClippedBy != null) {
    //   const [lClipContent, lClipParent] = this.getClippedBy();
    //   if (lClipContent != null) {
    //     return Frame.fromBounds(lClipContent.preBounds_()).transformed(lClipContent.matrix_()).transformed(matrix);
    //   }
    // }

    if( matrix.isAlmostIsotropic() ) {
      return Frame.fromBounds(this.preBounds_()).transformed(matrix);
    }
    else {
      return this._computeAnisotropicFrame('matrix_','vectorFrame_','preBounds_','preVectorBounds_');
    }
  },
  globalVectorFrame_() {
    // [TODO] use vectorFrame_
    return Frame.fromBounds(this.globalPreVectorBounds_()).transformed( this.globalTopMatrix_() );
  },
  globalFrame_() {
    const matrix = this.globalTopMatrix_();
    if( matrix.isAlmostIsotropic() ) {
      return Frame.fromBounds(this.globalPreBounds_()).transformed(matrix);
    }
    else {
      return this._computeAnisotropicFrame('globalTopMatrix_','globalVectorFrame_','globalPreBounds_','globalPreVectorBounds_');
    }
  },
  globalTopMatrix_() {
    let owner = this;
    while( owner._owner instanceof Figure.Group ) {
      owner = owner._owner;
    }
    return owner.matrix_();
  },
  _computeAnisotropicFrame( matrix, vectorFrame, preBounds, preVectorBounds ) {
    const urb = this[preBounds]();
    const ub  = this[preVectorBounds]();
    const olx =  ub.lx-urb.lx;
    const ohx = urb.hx- ub.hx;
    const oly =  ub.ly-urb.ly;
    const ohy = urb.hy- ub.hy;
    const s = this[matrix]().scaleFactor();
    return this[vectorFrame]().extended(s*olx,s*oly,s*ohx,s*ohy);
  },

  computedPreScale_ : function() {
    return this._computedPreScale.clone();
  },

  prePin$() {
    return this.update$().then( this.prePin_.bind(this) );
  },
  preVectorBounds$() {
    return this.update$().then( this.preVectorBounds_.bind(this) );
  },
  preBounds$() {
    return this.update$().then( this.preBounds_.bind(this) );
  },

  matrix$() {
    return this.update$().then( this.matrix_.bind(this) );
  },
  vectorBounds$() {
    return this.update$().then( this.vectorBounds_.bind(this) );
  },

  bounds$( v ) {
    if( v === undefined ) {
      return this.update$().then( this.bounds_.bind(this) );
    }
    else {
      return this.fit$(v);
    }
  },

  materialBounds$() {
    return this.update$().then( this.materialBounds_.bind(this) );
  },

  globalPreMatrix$() {
    return this.update$().then( this.globalPreMatrix_.bind(this) );
  },
  globalPreVectorBounds$() {
    return this.update$().then( this.globalPreVectorBounds_.bind(this) );
  },
  globalPreBounds$() {
    return this.update$().then( this.globalPreBounds_.bind(this) );
  },

  globalMatrix$() {
    return this.update$().then( this.globalMatrix_.bind(this) );
  },
  globalVectorBounds$() {
    return this.update$().then( this.globalVectorBounds_.bind(this) );
  },
  globalBounds$() {
    return this.update$().then( this.globalBounds_.bind(this) );
  },

  globalMaterialBounds$() {
    return this.update$().then( this.globalMaterialBounds_.bind(this) );
  },

  globalCenter$() {
    return this.globalBounds$().then( bounds => bounds.center );
  },

  prePin_() {
    return this._cache.get('prePin');
  },
  preVectorBounds_() {
    return this._cache.get('preVectorBounds');
  },
  preBounds_() {
    return this._cache.get('preBounds');
  },

  matrix_() {
    return this._cache.get('matrix');
  },
  vectorBounds_() {
    return this._cache.get('vectorBounds');
  },
  bounds_() {
    return this._cache.get('bounds');
  },
  materialBounds_() {
    return this._cache.get('materialBounds');
  },

  globalPreMatrix_() {
    return this._cache.get('globalPreMatrix');
  },
  globalPreVectorBounds_() {
    return this._cache.get('globalPreVectorBounds');
  },
  globalPreBounds_() {
    return this._cache.get('globalPreBounds');
  },

  globalMatrix_() {
    return this._cache.get('globalMatrix');
  },
  globalVectorBounds_() {
    return this._cache.get('globalVectorBounds');
  },
  globalBounds_() {
    return this._cache.get('globalBounds');
  },
  globalMaterialBounds_() {
    return this._cache.get('globalMaterialBounds');
  },

  boundsComputed() {
    return this._cache.has('bounds');
  },

// If Config.computeFigureGeometryLocally is false (i.e. IE8) then we should never get to these functions

// Functions triggered by the cache to compute bounds metrics
// 1) .bounds$() => .update$() then...
// 2) .bounds_() => return cached value or...
// 3) .computeBounds_() => forward to...
// 4) ._processor.computeBounds() => depending on the processor:
//       a) Processor:        forward to .outputFigure._primitive_computeBounds_()
//       b) OutputProcessor:  forward directly to ._primitive_computeBounds_()
//       c) GroupProcessor:   Figures.bounds_( .figures )

// preBounds: bounds of the isolated figure without including its own matrix
// bounds: bounds of the isolated figure, if it is not grouped is the same as the figure globalPreBounds
//         If the parent group is not grouped is the same as the globalBounds
// globalPreBounds: bounds of the figure without the top most group or figure transform
//                  Used to compute the frame
// globalBounds: bounds of the figure in its final position in the document once that all
//               group transforms have been applied.

  boundsIsVectorBounds() {
    return false;
  },

  _computePrePin() {
    return this._processor.computePrePin();
  },

// Testing, without optimizations
/*
, _computePreVectorBounds     () { return this._processor.computePreVectorBounds(); }
, _computePreBounds           () { return this._processor.computePreBounds(); }
, _computeVectorBounds        () { return this._processor.computeVectorBounds(); }
, _computeBounds              () { return this._processor.computeBounds(); }
, _computeGlobalPreVectorBounds() { return this._processor.computeGlobalPreVectorBounds(); }
, _computeGlobalPreBounds     () { return this._processor.computeGlobalPreBounds(); }
, _computeGlobalVectorBounds  () { return this._processor.computeGlobalVectorBounds(); }
, _computeGlobalBounds        () { return this._processor.computeGlobalBounds(); }
*/

  _computePreVectorBounds() {
    return this._processor.computePreVectorBounds();
  },

  _computePreBounds() {
    if( this.boundsIsVectorBounds() ) {
      // console.log('>>> optimize preBounds_ using preVectorBounds_');
      return this.preVectorBounds_().clone();
    }
    return this._processor.computePreBounds();
  },

  _computeVectorBounds() {
    const parent = this._owner;
    if( parent instanceof Figure.Group ) {
      if( parent._owner instanceof Figure.Group ) {
        const b = this._optimizeVectorBounds(this.matrix_());
        return b || this._processor.computeVectorBounds();
      }
      else {
        return this.globalPreVectorBounds_().clone();
      }
    }
    else {
      return this.globalVectorBounds_().clone();
    }
  },
  _computeBounds() {
     const parent = this._owner;
     if( parent instanceof Figure.Group ) {
       if( this.boundsIsVectorBounds() ) {
         // console.log('>>> optimize bounds_ using vectorBounds_');
         return this.vectorBounds_().clone();
       }
       if( parent._owner instanceof Figure.Group ) {
         const b = this._optimizeBounds(this.matrix_());
         return b || this._processor.computeBounds();
       }
       else {
         return this.globalPreBounds_().clone();
       }
     }
     else {
       return this.globalBounds_().clone();
     }
  },

  _computeMaterialBounds() {
     return this._processor.computeMaterialBounds();
   },

  _computeGlobalPreVectorBounds() {
    if( this._owner instanceof Figure.Group ) {
      const b = this._optimizeVectorBounds(this.globalPreMatrix_());
      return b || this._processor.computeGlobalPreVectorBounds();
    }
    else {
      return this.preVectorBounds_().clone();
    }
  },
  _computeGlobalPreBounds() {
    if( this._owner instanceof Figure.Group ) {
      if( this.boundsIsVectorBounds() ) {
        // console.log('>>> optimize globalPreBounds_ using vectorBounds_');
        return this.globalPreVectorBounds_().clone();
      }
      const b = this._optimizeBounds(this.globalPreMatrix_());
      return b || this._processor.computeGlobalPreBounds();
    }
    else {
      return this.preBounds_().clone();
    }
  },

  _computeGlobalVectorBounds() {
    const b = this._optimizeVectorBounds(this.globalMatrix_());
    return b || this._processor.computeGlobalVectorBounds();
  },
  _computeGlobalBounds() {
    if( this.boundsIsVectorBounds() ) {
      // console.log('>>> optimize bounds_ using vectorBounds_');
      return this.globalVectorBounds_().clone();
    }
    const b = this._optimizeBounds(this.globalMatrix_());
    return b || this._processor.computeGlobalBounds();
  },

  _computeGlobalMaterialBounds() {
    return this._processor.computeGlobalMaterialBounds();
  },

  // Groups disable this optimization
  _optimizeVectorBounds( t ) {
    if( t.s01 === 0 && t.s10 === 0 ) {
      // console.log('>>> optimize vectorBounds_ using preVectorBounds_');
      const ub = this.preVectorBounds_();
      return ub.scalingTransformed(t);
    }
    else {
      return null;
    }
  },
  _optimizeBounds( t ) {
    if( t.s00 === t.s11 && t.s01 === 0 && t.s10 === 0 ) {
      //console.log('>>> optimize bounds_ using preBounds_');
      const ub = this.preBounds_();
      return ub.scalingTransformed(t);
    }
    else {
      return null;
    }
  },

  _computePreVectorBoundsAfter( t ) {
    return this._processor.computePreVectorBoundsAfter(t);
  },
  _computePreBoundsAfter( t ) {
    return this._processor.computePreBoundsAfter(t);
  },

// abstract _primitive_computePreVectorBounds    () {}
// abstract _primitive_computePreBounds          () {}
// abstract _primitive_computePreVectorBoundsAfter( transform ) {}
// abstract _primitive_computePreBoundsAfter      ( transform ) {}


  _primitive_computeMaterialBounds() {
    return this._primitive_computePreMaterialBoundsAfter( this.matrix_() );
  },

  _primitive_computePreMaterialBoundsAfter( transform ) {
    return this._primitive_computePreBoundsAfter(transform) ;
  },

  _primitive_computeVectorBounds() {
    return this._primitive_computePreVectorBoundsAfter( this.matrix_() );
  },
  _primitive_computeBounds() {
    return this._primitive_computePreBoundsAfter( this.matrix_() );
  },

  _primitive_computeGlobalPreVectorBounds() {
    return this._primitive_computePreVectorBoundsAfter( this.globalPreMatrix_() );
  },
  _primitive_computeGlobalPreBounds() {
    return this._primitive_computePreBoundsAfter( this.globalPreMatrix_() );
  },

  _primitive_computeGlobalVectorBounds() {
    return this._primitive_computePreVectorBoundsAfter( this.globalMatrix_() );
  },
  _primitive_computeGlobalBounds() {
    return this._primitive_computePreBoundsAfter( this.globalMatrix_() );
  },
  _primitive_computeGlobalMaterialBounds() {
    return this._primitive_computePreMaterialBoundsAfter( this.globalMatrix_() );
  },

  _primitive_computePrePin() {
    const { anchor } = this;
    const pos = anchor._pos.clone();
    return anchor._absolute ? pos : this.preVectorBounds_().interpolate( pos ) ;
  },

// Functions triggered by the cache to compute matrix

  // If prePin is given, it is used instead of the cached one
  _computeMatrix( prePin ) {

    const pin      = this._pin ;
    const matrix22 = this instanceof Figure.Raster 
                       ? Matrix.m(this._preMatrix22, this._matrix22)
                       : this._matrix22;

    // We currently have two pinning system in CDL:
    // Bounds based pinning (traditional system):
    //   In this system the figure is first transformed and constrained. Afterwards
    //   the bounds of the transformed figure are calculated and is used to compute
    //   the reference point (with pin.relPos) that will be moved to pin.pos
    // Anchor based pinning (new system):
    //   Figures have an anchor (reference point) that can be absolute or relative
    //   to the preVectorBounds. The figure is transformed with the anchor as pivot
    //   and later moved to pin.pos using the anchor as reference. This avoids the
    //   problems with the bounds based pinning system when there are rotations and
    //   the reference point start jumping from place to place.

    const boundsBasedPinning = ( pin && ! pin.anchorBased() ) ;

    // prePin_() could require an expensive call to _computeVectorBounds() and in the
    // case of bounds based pinning it will be ignored anyways at the end, so better to
    // avoid it. This produces a noticeable effect in the user experience.
    const anchor = boundsBasedPinning ? new Point(0,0)
                                    : prePin || this.prePin_();

    // Move the figures to the origin, and transform them there
    let matrix = Matrix.m( matrix22, Matrix.translate(Point.minus(anchor)) ) ;

    if( ! pin ) {
      return matrix; // Remove this guard, the pin is always defined
    }

    // Account for old bounds based pinning, this will be removed at one point
    if( boundsBasedPinning ) {
      const offset = this._computePreVectorBoundsAfter(matrix).interpolate(pin.getRelPos());
      matrix = Matrix.m( Matrix.translate(Point.minus(offset)), matrix );
    }

    // Translate the origin to the pin
    matrix = Matrix.m( Matrix.translate( this.pin ), matrix ) ;

    return matrix;
  },

  _computeGlobalPreMatrix() {
    const parent = this._owner;
    if( parent instanceof Figure.Group ) {
      const t = this.matrix_();
      return Matrix.m( this._owner.globalPreMatrix_(), t );
    }
    else {
      return new Matrix();
    }
  },
  _computeGlobalMatrix() {
    const t = this.matrix_();
    return this._owner instanceof Figure.Group ? Matrix.m( this._owner.globalMatrix_(), t ) : t ;
  },

  processPowerClips$() {
    return invokeMap$(this.deepPowerClipContents(),'update$');
  },

  generateOutputFigure$() {
    const _this = this;

    // Cache all data before starting, in case the user starts changing the figure
    const preMatrix22 = _this._preMatrix22.clone();
    const processes   = _this._processes.cloneDeep();

    const matrix22    = _this.matrix22.clone();
    const pin         = _this.pin.clone();
    const alpha       = _this.alpha;

    return _this.inputPrimitiveFigures$() .then( input => {

    input.preScale = new Point(1,1);

    if( input.figures.length === 0 ) {
      return _this.createEmptyOutput();
    }

    if( ! preMatrix22.isIdentity() ) {
      const ta = Matrix.transformAround(input.anchor,preMatrix22);
      forEach( input.figures, figure => {
        figure.transform(ta);
      });
    }

    // Allow forwarding of textLayouts for non text processes, so they can be edited
    input.textLayouts = filter(input.figures,'isTextLayout');

    // output is one Figure (possible a group)
    const output = processes.process(input);

    _this._computedPreScale = output._computedPreScale;
    // console.log(_this._computedPreScale.textualize());

    output.alpha = alpha;

    // Review this two... should we use the latest ones here?
    output.matrix22 = matrix22;
    output.pin = pin;

    output.clippedBy = _this.clippedBy;
    output.clipping = _this.clipping;
    output.clipAbsolute = _this.clipAbsolute;
    
    // console.log('Finish text processing');

    return output;

    });
  },

  addClippingRect$(aId) {
    const lDoc    = this.document;
    const me = this;
    return this.preBounds$().then( bounds => {
        let figure = Figure.Shape.CreateRectangle({
                                                center:new Point(bounds.mx,bounds.my)
                                              , width:bounds.width*0.9
                                              , height:bounds.height*0.9
                                              , brush: { color: { rgb: '000000', alpha: 0.0 } } 
                                              });
        lDoc.add(figure);
        figure.clipping = true;
        figure.id = aId;
        me.clippedBy = aId;
        return me.beginEditClip$();
        });
  },
  
  beginEditClip$() {
    const [lClipContent, lClipParent] = this.getClippedBy();
    if (lClipContent) {
      if (!lClipContent.clipAbsolute  && !lClipContent.editingClip) {
        lClipContent.transform(lClipParent.globalMatrix_());
      }
      lClipContent.editingClip = true;
    }
    return this.document.preview$();
  },

  endEditClip$() {
    const [lClipContent, lClipParent] = this.getClippedBy();
    if (lClipContent) {
      if (!lClipContent.clipAbsolute) {
        lClipContent.transform(Matrix.invert(lClipParent.globalMatrix_()));
      }
      lClipContent.editingClip = false;
    }
    return this.document.commit$();
  },

  clippingHidden(){
    return this.getClipping() && !this._editingClip;
  },

  getClippedBy() {
    let o = this;
    let lClippedBy = this.clippedBy;
    let lClipParent = this;//the node that the clipped is relative to
    while( o && !o.isLayer && !lClippedBy) {
      var oo = o._owner;
      if ( oo && oo.isGroup ) {
        lClippedBy = oo.clippedBy;
        lClipParent = oo;
      }
      o = oo;
    }

    let lClipContent = null;
    if( lClippedBy !== null ) {
      const lDoc = this.document;
      const lFigures = lDoc.figures;
      lClipContent = find( lFigures, { _id: lClippedBy } );
    }

    return [lClipContent, lClipParent];
  },

  getClipping() {
    let o = this;
    let lClipping = this.clipping;
    while( o && !o.isLayer && !lClipping) {
      var oo = o._owner;
      if ( oo && oo.isGroup ) {
        lClipping = oo.clipping;
      }
      o = oo;
    }
    return lClipping;
  },

  rwPowerClipTransform() {
    // The powerclip transform is simpler than the one needed for polyregions
    // Because the powerclip figures are always based against the origin, we
    // just need to apply the matrix22 directly and then move the result to
    // the pin position. The anchor of the figures doesn't play a role here
    const o = this._owner,
          t = Matrix.m( Matrix.translate(this.pin), this._matrix22 );
    return o instanceof Figure.Group ? Matrix.m( o.globalMatrix_(), t ) : t;
  },

  grouped() {
    return this._owner instanceof Figure.Group;
  },

  canEditContent() {
    return this._powerClipContents.length > 0;
  },

  createEmptyOutput() {
    return Figure.Shape.CreateRectangle({center:this.pin,width:100,height:100,brush:Brush.empty});
  },

  get document() {
    let o = this._owner;
    while( o ) {
      if( o.isDocument ) {
        return o;
      }
      o = o._owner;
    }
    return null;
  },

  get page() {
    let o = this._owner;
    while( o ) {
      if( o.isPage ) {
        return o;
      }
      o = o._owner;
    }
    return null;
  },

  get layer() {
    let o = this._owner;
    while( o ) {
      if( o.isLayer ) {
        return o;
      }
      o = o._owner;
    }
    return null;
  },

  // Return the top input figure
  get topFigure() {
    let o = this;
    while( o ) {
      const oo = o._owner;
      if( oo && oo.isLayer ) {
        return o._inputFigure || o;
      }
      o = o._owner;
    }
    return null;
  },

  shouldDrawTextureType( textureType ) {
    return textureType === 'color';
  },

  outputsText() {
    return false;
  },

  quickToDraw() {
    return false;
  },

  canBeProcessedLocally() {
    return false;
  },

  supportsLocalMetrics() {
    return true;
  },

  usingPowerClipLink() {
    return this.canBeProcessedLocally();
    // Possible optimization:
    // When we use server processing, and there is only one powerclip content we manually
    // set up the PowerClipLink at the output
    //  || ( ! this.isGroup && this._powerClipContents.length < 2 );
  },

  init( p = {} ) {
    const _this = this;

    _this.callParent(p);
    _this._localId = uniqueId();

    _this._inputFigure = null;

    _this._cache = new FigureCache({
      preReferencePoint     : _this._computePreReferencePoint,

      prePin                : _this._computePrePin,
      preVectorBounds       : _this._computePreVectorBounds,
      preBounds             : _this._computePreBounds,
      matrix                : _this._computeMatrix,
      vectorBounds          : _this._computeVectorBounds,
      bounds                : _this._computeBounds,
      materialBounds        : _this._computeMaterialBounds,
      globalPreMatrix       : _this._computeGlobalPreMatrix,
      globalPreVectorBounds : _this._computeGlobalPreVectorBounds,
      globalPreBounds       : _this._computeGlobalPreBounds,
      globalMatrix          : _this._computeGlobalMatrix,
      globalVectorBounds    : _this._computeGlobalVectorBounds,
      globalBounds          : _this._computeGlobalBounds,
      globalMaterialBounds  : _this._computeGlobalMaterialBounds
      // [TODO] globalMaterialBounds ?

    },_this);

    // Pre scale is used to compute the concrete font size, it is the
    // approximate scaling in x and y of the figure after the preTransform
    // and being processed. It doesn't include the final transformation.
    // We do not add this to the cache because it is not something that can
    // be computed directly from the output figure. It needs to be computed
    // during the processing of the effect stack and saved
    _this._computedPreScale = new Point(1,1);

    // Most of this will be eliminated once we no longer needs to support bounds based pinning
    _this._pin = p.pin ? p.pin instanceof Pin ? p.pin : new Pin(p.pin,true) : new Pin() ;

    _this._bc = {};

    _this._processor = new Processor(_this);

    _this._lockedTemporally = 0; // [TODO] make a helper for all this push, pop logic

    _this.initEvents();
  },

  initEvents() {
    this.addEvents(
      'rendercomplete',
      'processingstarted',
      'processingcomplete',
      'processingfailed',
      'serverinvalidate',
      'redrawneeded'
    );
  },

  _ignoreAttributes : [],

  _fixUpPin$( referencePoint,previousReferencePoint) {
    if( ! this._outputFigure ) {
      return resolve(); // Hack, we will remove bounds based pinning hopefully soon
    }
    const previousPoint = previousReferencePoint || this[referencePoint]();
    return this.update$().then( () => {
    const newPoint = this[referencePoint]() ;
    const offset = previousPoint.minus(newPoint);
    // console.log('[point] previous: '+previousPoint.toXmlString() + ' > new: '+newPoint.toXmlString()+' > offset: '+offset.toXmlString());
    this.translate( offset.x, offset.y ) ;
    });
  },

  fastTranslate( dx, dy ) {
    if( dy === undefined ) {
      const p=dx; dx=p.x; dy=p.y;
    }
    if( ! Config.useSubpixelPinForServerRendering ) {
      this.translate(dx,dy);
      return;
    }
    const translate = Matrix.translate(dx, dy);
    translate.applyInplace(this._pin);
    this._processor.fastTranslate(dx, dy);
    return this;
  },

  translate( dx, dy ) {
    if( dy === undefined ) {
      const p=dx; dx=p.x; dy=p.y;
    }
    const translate = Matrix.translate(dx, dy);
    translate.applyInplace(this._pin);
    this._processor.translate(dx, dy);
    return this;
  },

  scaleFrom( point, sx, sy, deg ) {
    const scaling = Matrix.scaleFrom(point, sx, sy, deg);
    scaling.applyInplace(this._pin);
    this._matrix22 = Matrix.m( Matrix.scale(sx,sy,deg), this._matrix22 );
    this._processor.scaleFrom(point, sx, sy, deg);
    this.checkForTransformIndependent();
    return this;
  },

  checkForTransformIndependent() {
    if (this._processes.length>0)
    {
      if (this._processes.find (p => p._transformIndependent)) {
        this._invalidateOnCommit = true;
      }
    }
  },

  rotateAround( point, deg ) {
    return this.transform( Matrix.rotateAround(point,deg) );
  },

  shearAround( point, sx, sy, deg ) {
    return this.transform( Matrix.shearAround(point, sx, sy, deg) );
  },

  transform( matrix ) {
    this._matrix22 = Matrix.m(matrix.toMatrix22(), this._matrix22);
    matrix.applyInplace(this._pin);
    this._processor.transform(matrix);

    if (!matrix.isAlmostIsotropic() )
      this.checkForTransformIndependent();
  
    return this;
  },

  _align$( alignment, toBounds, margin ) {
    return this.bounds$().then( bounds => {
    return this.translate( Bounds.alignOffset(bounds,alignment,toBounds,margin) );
    });
  },

  _fineHitTest( /*point, transform, options*/ ) {
    return true;
  },

  _representationType() {
    return 'server';
  },

  _aliasedOutput() {
    return this._outputFigure === this;
  },

  _onOutputFigureChange() {
    const of = this._outputFigure;
    if( of && this !== of ) {
      of._processor = new OutputProcessor(of);
      // Important, the cache of the output figure points to the cache of the input figure.
      // Apart from being a good optimization, it allows us to clone output figures without
      // worring about caches
      of._cache = this._cache;
      // The owner of the output is the same owner as the input figure. This is used to
      // properly comply with the adopting mechanism and because the figure needs to know
      // if it is part of a group when computing its matrix
      of._owner = this._owner;
      // The inputFigure property is used when computing the topFigure of output polyregions
      of._inputFigure = this;
    }
  },

// overwrite g.OwnedObject owner() function
  owner( v ) {
    if(v===undefined) {
      return this._owner;
    }
    this._owner=v;
    const of = this._outputFigure;
    if( of && this !== of ) {
      of._owner=v;
    }
    return this;
  },

  topColors( colors) {
    if( this._pen ) {
      this._pen.getColors(colors);
    }
    if( this._brush ) {
      this._brush.getColors(colors);
    }
  },

  otherColors( /*colors*/) {},

  otherPens( /*pens*/) {},

  topBrushes( brushes) {
    brushes.push( this._brush );
  },
  otherBrushes( /*brushes*/) {},

  hasPreview() {
    return true;
  },

  needsCacheToRender() {
    return true;
  },

  representationType() {
    const owner = this._owner; // If the owner is hidden, do not show the figure
    if( this.visible && ! ( owner && owner.hidden ) ) {
      const rt = this._processor.representationType() ;
      return rt;
    }
    else {
      return 'hidden';
    }
  },

  // TextureBrush TransformMode Fixed needs to be redrawn even after exact pixel translation
  needsFixedDrawing() {
    // return some( this.getBrushes(), b => b.needsFixedDrawing() );
    const brushes = this.getBrushes();
    for( let k = 0, kEnd = brushes.length; k < kEnd; ++k ) {
      if( brushes[k].needsFixedDrawing() ) {
        return true;
      }
    }
    if (this.clippedBy) {
      return true;
    }
    return false;
  },

  deepPowerClipContents() {
    return this._powerClipContents;
  },

  powerClipsLocalRendering() {
    const pccs = this._powerClipContents;
    if( pccs.isEmpty ) {
      return true;
    }
    if( Config.renderPowerClipsLocally ) {
      if( every( pccs, pcc => every( pcc.figures, figure => ! figure.needsCacheToRender() ) ) ) {
        return true;
      }
    }
    return false;
  },

  redraw$() {
    const _this = this;
    // [TODO] Switching to ES6 promises exposes a bug with locking while
    // using the SelectTool. This bug will disapear once we swtiching to using
    // preview$ in tools
    _this.invalidateView(true);
    return _this.render$() .then( () => {
    _this.fireEvent('redrawneeded',_this);
    });
  },

  _prepareToDraw$( zoom ) {
    return resolve(zoom);
  },

  prepareToDraw$( zoom ) {
    // Ask for a bigger image if need it
    const outputFigure = this._outputFigure;
    if( outputFigure ) {
      return outputFigure._prepareToDraw$(zoom);
    }
    else {
      // Check, if we should call update$ here
      return resolve(zoom);
    }
  },

  _preparedToDraw( /*zoom*/) {
    return true;
  },
  preparedToDraw( zoom ) {
    const of = this._outputFigure;
    return of && of._preparedToDraw(zoom);
  },

  draw( context, transform, config = {}, ignoreAlpha = false ) {
    // We reuse the figure.draw() function to draw powerclips and grouped
    // figures by passing as parameter a transform that doesn't correspond with
    // the transformation from doc coordinates to the final viewport.
    // TextureBrush between other elements requires to know this transformation
    // for fixed and non scaling modes.
    if( ! config.transformDoc ) {
      config = assign({ transformDoc: transform },config);
    }

    const changeGlobalAlpha = ( ! ignoreAlpha && this._alpha !== 1.0 );
    if( changeGlobalAlpha ) {
      context.save();
      context.globalAlpha *= this._alpha;
    }
    this._processor.draw(context,transform,config);

    if( changeGlobalAlpha ) {
      context.restore();
    }
  },

  clipContext( context, transform ) {
    context.beginPath();
    this.drawForClipping(context, transform);
    context.clip( );
  },

  drawForClipping( context, transform ) {
      //Each figure knows how to draw itself for clipping
  },

  _draw( context,transform/*,config*/) {
    const p = this.pin;
    const pt = transform.apply(p);
    context.fillRect(pt.x-10,pt.y-10,20,20);
  },

  // [TODO] Support for stopping iteration, refactor filter using foreach

  deepForEachFigureLeave( f, context ) {
    if( this.isGroup ) {
      const groupedFigures = this._figures;
      //_.forEach(groupedFigures,function(gf) {
      for(let k=0,kEnd=groupedFigures.length;k<kEnd;++k) {
        groupedFigures[k].deepForEachFigureLeave(f,context);
      }//);
    }
    else {
      f.call(context,this);
    }
  },

  deepFindFigureLeave( f, context ) {
    if( this.isGroup ) {
      const groupedFigures = this._figures;
      //_.forEach(groupedFigures,function(gf) {
      for(let k=0,kEnd=groupedFigures.length;k<kEnd;++k) {
        if( groupedFigures[k].deepFindFigureLeave(f,context) ) {
          return true;
        }
      }//);
      return false;
    }
    else {
      return f.call(context,this);
    }
  },

  deepForEachFigure( f, context ) {
    if( f.call(context,this) === false ) {
      return false;
    }
    if( this.isGroup ) {
      const groupedFigures = this._figures;
      //_.forEach(groupedFigures,function(gf) {
      for(let k=0,kEnd=groupedFigures.length;k<kEnd;++k) {
        if( groupedFigures[k].deepForEachFigure(f,context) === false ) {
          return false;
        }
      }//);
    }
  },

  deepFindFigure( f, context ) {
    if( f.call(context,this) ) {
      return true;
    }
    if( this.isGroup ) {
      const groupedFigures = this._figures;
      //_.forEach(groupedFigures,function(gf) {
      for(let k=0,kEnd=groupedFigures.length;k<kEnd;++k) {
        if( groupedFigures[k].deepFindFigure(f,context) ) {
          return true;
        }
      }//);
      return false;
    }
    return false;
  },

  deepForEachPolyregion( f ) {
    this.deepForEachFigureLeave( leave => {
      if( leave.isShape ) {
        forEach(leave._polyregions,f);
      }
    });
  },

// [J-API]
//------------------------------------------------------------------------
// The following functions will probably change in the near future

// options : { width, height, locked:true, pin: figurePin }
  setSize$( options ) {
    return Figures.setSize$([this],options.pin?options: assign({pin:this.pin},options) );
  },
  fit$( bounds, options ) {
    return Figures.fit$([this],bounds,options); // [TODO]
  },

  getFirstEnvelopePoint() {
    const p = findByType(this._processes,Process.FitEnvelope);
    return p ? this.matrix_().apply( p._envelope.getFirstPoint() ) : null;
  },

  getPens( pens) { if( pens === undefined ) { pens = []; }
    pens.push(this._pen);
    this.otherPens(pens);
    this._processes.getPens(pens);
    const pccs = this._powerClipContents;
    //_.forEach(pccs,function(pcc) {
    for(let k=0,kEnd=pccs.length;k<kEnd;++k) {
      pccs[k].getPens(pens);
    }//);
    return pens;
  },

  getBrushes( brushes = [] ) {
    this.topBrushes(brushes);
    this.otherBrushes(brushes);
    this.processes.getBrushes(brushes);
    forEach( this.powerClipContents, pcc => {
      pcc.getBrushes(brushes);
    });
    return brushes;
  },
  uniqueBrushZones( options, zones = [] ) {
    const map = {};
    //_.forEach( zones, function(z) {
    for(let i=0,iEnd=zones.length;i<iEnd;++i) {
      const z = zones[i];
      map[z.value.textualize()] = z.value;
    }//);
    const brushes = this.getBrushes([],true);
    //_.forEach(brushes,function(c) {
    for(let k=0,kEnd=brushes.length;k<kEnd;++k) {
      const b = brushes[k];
      const bs = b.textualize() ;
      if( ! ( bs in map ) ) {
        map[bs] = b ;
        const zone = { value: b.clone() };
        zones.push(zone);
      }
    }//);
    return zones;
  },
  changeBrushZone( zone, value ) {
    this.replaceBrush(zone.value,value);
  },

  // BP
  untransformedVectorBounds$() {
    return this.preVectorBounds$();
  },

  extractPowerClipContents() { // [TODO][PC]
    const powerClipFigures = [];
    const powerClipContents = this._powerClipContents;
    forEach( powerClipContents, pcc => {
      const figures = pcc.extractFigures();
      forEach(figures, f => {
        powerClipFigures.push(f);
      });
    });

    this.powerClipClear();

    return powerClipFigures;
  },

  powerClipLinkClear() {
    // PowerClipLink currently used in Shape output
  },

  powerClipClear() {

    // Do not trigger invalidation, We can directly modify the output figure
    this._powerClipContents._clear();

    const o = this._outputFigure;
    if( o ) {
      if( o !== this ) {
        o._powerClipContents._clear();
        o.powerClipLinkClear();
      }
      // We are touching the processed figure directly, we can no longer
      // use the stored cacheId figure in the server
      this.cacheId = null;
      this.invalidateView();
    }
    else {
      this.invalidate();
    }
  },

  bc( key ) {
    return this._bc[key];
  }
});

Figure._baseParseXml = function(node,derived,bcFixes) {
  const me = this;

  this.setupProperties( Xml.parseProperties(node,derived) );

  this._metadata = Metadata.parseXml(node,this);

  const preTransformNode = Xml.child(node,'PreTransform');
  if( preTransformNode ) {
    this._preMatrix22 = Matrix.getMatrix22Attr(preTransformNode, 'Matrix22');
  }
  const figureTransform = FigureTransform.fromXml(node);
  me._matrix22 = figureTransform.matrix ;

  me._pin = Pin.fromXml(node);

  // Backward compatibility
  if( bcFixes !== undefined ) {

    // Bounds based pinning => Anchor based pinning
    //-------------------------------------------------------------------
    const pin = me._pin;
    if( pin.use !== 'Anchor' ) {
      const pinRelPos = pin.getRelPos();
      me._anchor._pos = Matrix.rotateAround( new Point(0.5,0.5), -me._matrix22.angle() ).apply( pinRelPos );

      me._anchor._absolute = false;
      pin.use = 'Anchor';

      bcFixes.push( () => {
        return me.vectorBounds$() .then( vectorBounds => {
        const boundsBasedAnchor = vectorBounds.interpolate(pinRelPos);
        me.translate( me.pin.minus( boundsBasedAnchor ) );
        });
      });
    }

    // Post Constraint
    //-------------------------------------------------------------------
    const postConstraint = figureTransform.postConstraint;
    if( postConstraint && ( postConstraint.x || postConstraint.y ) ) {
      me._bc.constrain = { width: postConstraint.x, height: postConstraint.y };
      bcFixes.push( () => {
        return me.bounds$() .then( bounds => {
        const c = { pin: me.pin, locked: false };
        if( bounds.width > postConstraint.x ) {
          c.width = postConstraint.x;
        }
        if( bounds.height > postConstraint.y ) {
          c.height = postConstraint.y;
        }
        if( c.width || c.height ) {
          return me.setSize$(c);
        }
        });
      });
    }
  }
};

Figure.parseXml = function (node,bcFixes) {
  if( node ) {
    switch(node.nodeName) {
    case 'TextBlock':  return Figure.Text      .parseXml(node, bcFixes);
    case 'Shape':      return Figure.Shape     .parseXml(node, bcFixes);
    case 'Raster':     return Figure.Raster    .parseXml(node, bcFixes);
    case 'Group':      return Figure.Group     .parseXml(node, bcFixes);
    case 'ProxyGroup': return Figure.ProxyGroup.parseXml(node, bcFixes);
    case 'Monogram':   return Figure.Monogram  .parseXml(node, bcFixes);
    }
  }
  return null;
};

Figure.arrayFromXml = function(node,bcFixes) {
  const figures = [];
  const figuresNode = Xml.child(node,'Figures');
  if( figuresNode ) {
    node = figuresNode;
  }
  forEach( Xml.children_(node), figureNode => {
    const figure = Figure.parseXml(figureNode,bcFixes);
    if( figure ) {
      figures.push(figure);
    }
  });
  return figures;
};

Figure.fromXml = function( node ) {
  const children = Xml.children_(node);
  for( let i = 0, iEnd = children.length; i < iEnd; ++i ) {
    const figure = Figure.parseXml(children[i]);
    if( figure ) {
      return figure;
    }
  }
};

Figure.fromXmlString = function(xmlString) {
  const domFigure = Xml.fromString(xmlString);
  return Figure.fromXml(domFigure.documentElement);
};

Figure.arrayBuildXml = function(node,v) {
  const figuresNode = Xml.appendElement(node,'Figures');
  Xml.addObjectArray(figuresNode,v);
};

export const Clipart = {}; // This will be moved from here

Figure.load$ = Clipart.load$ = function( docModel ) {
  return Figures.load$(docModel).then( figures => figures[0] );
};

Xml.Parser['Figure']   = Xml.makeObjectParser(Figure);
Xml.Parser['Figure[]'] = Xml.makeArrayObjectParser(Figure);



/** WEBPACK FOOTER **
 ** ./src/cdl/figure/figure.js
 **/